/**
 * SQL文内のテーブル名置換ユーティリティ
 *
 * DuckDB ASTから取得した位置情報（query_location）を使用して、
 * SQL文内のテーブル名を安全に置換する。
 *
 * 注意: DuckDBのjson_serialize_sql関数が返すquery_locationはUTF-8バイト位置である。
 * JavaScriptの文字列操作は文字位置（コードポイント）を使用するため、
 * 日本語など非ASCII文字を含むSQLではbytePositionToCharPosition関数で変換が必要。
 */

import type { TableReference } from '../duckdb';
import logger from '../logger';

/**
 * UTF-8バイト位置を文字位置（コードポイント）に変換する
 *
 * DuckDBのjson_serialize_sql関数はUTF-8バイト位置を返すが、
 * JavaScriptの文字列操作は文字位置を使用するため、この変換が必要。
 *
 * @param str 対象の文字列
 * @param bytePosition UTF-8バイト位置
 * @returns 文字位置（コードポイント）
 */
export function bytePositionToCharPosition(str: string, bytePosition: number): number {
  const encoder = new TextEncoder();
  let byteOffset = 0;

  for (let charPos = 0; charPos < str.length; charPos++) {
    if (byteOffset >= bytePosition) {
      return charPos;
    }
    // 各文字のUTF-8バイト長を加算
    byteOffset += encoder.encode(str[charPos]).length;
  }

  // バイト位置が文字列の終端以降の場合
  return str.length;
}

/**
 * 位置情報を使ってSQL文内のテーブル名を置換する
 *
 * @param sql 元のSQL文
 * @param references ASTから抽出したテーブル参照情報（バイト位置）
 * @param oldName 置換前のテーブル名
 * @param newName 置換後のテーブル名
 * @returns 置換後のSQL文
 */
export function replaceTableNamesAtLocations(
  sql: string,
  references: TableReference[],
  oldName: string,
  newName: string
): string {
  // oldNameに一致するテーブル参照のみをフィルタ（大文字小文字を区別しない）
  const matchingRefs = references.filter(
    ref => ref.tableName.toLowerCase() === oldName.toLowerCase()
  );

  if (matchingRefs.length === 0) {
    return sql;
  }

  // バイト位置を文字位置に変換
  const refsWithCharPosition = matchingRefs.map(ref => ({
    ...ref,
    charLocation: bytePositionToCharPosition(sql, ref.queryLocation)
  }));

  // 文字位置の降順でソート（後ろから置換することで位置ずれを防ぐ）
  const sortedRefs = [...refsWithCharPosition].sort(
    (a, b) => b.charLocation - a.charLocation
  );

  let result = sql;

  for (const ref of sortedRefs) {
    const location = ref.charLocation;

    // 期待される文字列: ダブルクォートで囲まれたテーブル名
    // DuckDBでは識別子はダブルクォートで囲む
    const expectedQuoted = `"${ref.tableName}"`;
    const actualSubstr = result.substring(location, location + expectedQuoted.length);

    if (actualSubstr.toLowerCase() === expectedQuoted.toLowerCase()) {
      // 新しいテーブル名でダブルクォート内のダブルクォートをエスケープ
      const escapedNewName = newName.replace(/"/g, '""');
      const replacement = `"${escapedNewName}"`;

      result =
        result.substring(0, location) +
        replacement +
        result.substring(location + expectedQuoted.length);

      logger.debug('[replaceTableName] Replaced table name', {
        byteLocation: ref.queryLocation,
        charLocation: location,
        oldName: ref.tableName,
        newName,
        before: actualSubstr,
        after: replacement
      });
    } else {
      // 位置が一致しない場合（通常は発生しないはず）
      // エラーとして記録し、ユーザーに気付かせる
      logger.error('[replaceTableName] Position mismatch, skipping replacement', {
        byteLocation: ref.queryLocation,
        charLocation: location,
        expected: expectedQuoted,
        actual: actualSubstr
      });
    }
  }

  return result;
}

/**
 * SQL文が特定のテーブル名を参照しているか判定
 *
 * @param references ASTから抽出したテーブル参照情報
 * @param tableName 検索するテーブル名
 * @returns テーブルが参照されているかどうか
 */
export function sqlReferencesTable(
  references: TableReference[],
  tableName: string
): boolean {
  return references.some(
    ref => ref.tableName.toLowerCase() === tableName.toLowerCase()
  );
}
